import type {
  IngestIntake,
  IngestIntakeTabularTarget,
  IngestMappingConfigSummary,
  TabularConfigBuildDetail,
  TabularConfigBuildPublicReason,
  TabularConfigDraft
} from '@axilon-apps/domains-ontology-ui-state'
import { formatCompactLocalTime, formatRelativeTime } from '../../../lib/time'
import { extensionList, refusedExtensions } from './configView'
import { agentScripts } from './subject'

const SEP = ' · '

/** Why a build ran, in a sentence fragment the meta line and banners share. */
export function reasonText(reason: TabularConfigBuildPublicReason): string {
  switch (reason.kind) {
    case 'initial':
      return reason.note ? `initial build: ${reason.note}` : 'initial build'
    case 'fingerprint_mismatch': {
      const first = reason.diff[0]
      if (!first) return 'fingerprint mismatch'
      const rest = reason.diff.length - 1
      return (
        `fingerprint mismatch: ${first.path} was ${quote(first.expected)}, now ${quote(first.found)}` +
        (rest > 0 ? ` and ${rest} more` : '')
      )
    }
    case 'run_failure':
      return `run failed: ${reason.message}`
    case 'contract_edit':
      return `contract edited: ${reason.note}`
    case 'new_examples':
      return `new examples: ${reason.note}`
    case 'instruction':
      return `instruction: ${reason.note}`
    case 'description_edit':
      return `description edited: ${reason.note}`
  }
}

/** A description edit that was not sent back: no builder runs, the verifier re-reads the pages. */
export function verifiesOnly(build: TabularConfigBuildDetail): boolean {
  return build.reason.kind === 'description_edit' && !build.priorDraft
}

/** The leading characters of a job id — enough to tell builds apart in a sentence. */
export function shortId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id
}

function revisedFrom(build: TabularConfigBuildDetail): string[] {
  const prior = build.priorDraft
  return prior ? [`revised from build ${shortId(prior.buildJobId)}: "${prior.note}"`] : []
}

export function versionMeta(config: IngestMappingConfigSummary, now = Date.now()): string {
  const { definition } = config
  return [
    `v${config.version}`,
    `saved ${formatRelativeTime(config.created_at, { now, fallback: config.created_at })} by ${definition.parser.approvedBy}`,
    `"${definition.parser.changeReason}"`,
    `writes to urn:db:${config.name}`,
    `accepts ${definition.input?.accept?.join(', ') ?? 'any'}`
  ].join(SEP)
}

/** A draft changes which intake files are refused only when the accepted set changes. */
export function acceptedTypesChange(
  draft: TabularConfigDraft,
  base: IngestMappingConfigSummary | undefined
): string | undefined {
  if (!base) return undefined
  const current = draft.input?.accept
  const previous = base.definition.input?.accept
  const normalized = (accept: string[] | undefined) =>
    JSON.stringify(accept?.map((extension) => extension.toLowerCase()).sort())
  if (normalized(current) === normalized(previous)) return undefined
  return `accepts ${current?.join(', ') ?? 'any'} (v${base.version}: ${previous?.join(', ') ?? 'any'})`
}

/**
 * Intakes on the config's latest version that take file types the draft
 * refuses, so those files fail once it is saved. A pinned intake keeps its
 * version.
 */
export function refusingIntakesWarning(
  configName: string,
  draft: Pick<TabularConfigDraft, 'input' | 'proposedVersion'>,
  intakes: readonly IngestIntake[]
): string | undefined {
  const affected = intakes.flatMap((intake) => {
    if (intake.targetKind !== 'mapped_tabular_ingest' || !intake.target) return []
    const target = intake.target as IngestIntakeTabularTarget
    if (target.configName !== configName || target.configVersion) return []
    const refused = refusedExtensions(draft.input?.accept, intake.accept)
    return refused.length > 0 ? [{ name: intake.name, refused }] : []
  })
  const version = `v${draft.proposedVersion}`
  const [first] = affected
  if (!first) return undefined
  if (affected.length === 1) {
    return `Intake ${first.name} also takes ${extensionList(first.refused)} files, which ${version} does not accept. They fail once ${version} is saved.`
  }
  const named = affected.map((entry) => `${entry.name} (${entry.refused.join(', ')})`)
  return `Intakes ${extensionList(named)} take file types ${version} does not accept. Those files fail once ${version} is saved.`
}

export function draftMeta(
  build: TabularConfigBuildDetail,
  draft: TabularConfigDraft,
  now = Date.now()
): string {
  const parts = [
    `draft v${draft.proposedVersion}`,
    `${draft.mode} mode`,
    reasonText(build.reason),
    ...revisedFrom(build),
    `built ${formatRelativeTime(build.updatedAt, { now, fallback: build.updatedAt })}`,
    `${build.result.agentTurns} turns`
  ]
  if (draft.parser.kind === 'agent') {
    const pages = (draft.pagePreviews ?? []).filter((page) => page.status !== 'blank')
    const unmatched = pages.filter(
      (page) => page.status === 'mismatch' || page.status === 'unreadable'
    ).length
    const scripts = agentScripts(draft.parser).map((script) => script.entrypoint)
    parts.push(
      `trial extraction on ${pages.length} page${pages.length === 1 ? '' : 's'}`,
      draft.verification.status !== 'passed'
        ? 'verification failed'
        : unmatched > 0
          ? `${unmatched} not matched`
          : 'all match',
      scripts.length > 0
        ? `scripts ${scripts.join(', ')}, no fingerprint`
        : 'no script, no fingerprint'
    )
  } else {
    const examples = draft.verification.examples.length
    parts.push(
      draft.verification.status === 'passed' ? 'verified deterministic' : 'verification failed',
      `fingerprint holds on ${examples} example${examples === 1 ? '' : 's'}`
    )
  }
  return parts.join(SEP)
}

export function buildingMeta(build: TabularConfigBuildDetail): string {
  return [
    verifiesOnly(build) ? 'verifying' : 'builder running',
    reasonText(build.reason),
    ...revisedFrom(build)
  ].join(SEP)
}

/** A timestamp the way the rest of the UI shows one ("Sep 15, 4:00 AM"); the raw string if unparseable. */
export function formatWhen(iso: string): string {
  return formatCompactLocalTime(iso, { fallback: iso })
}

function quote(value: string | undefined): string {
  return value === undefined ? '(missing)' : `\`${value}\``
}
